<template>
  <div class="isolate mm-root t24-tools mm-clips clips-ops-page">
    <section class="clips-ops-shell">
      <header class="clips-ops-header">
        <div>
          <NuxtLink class="clips-ops-back" to="/clips">
            <span class="i-lucide-arrow-left" aria-hidden="true"></span>
            {{ t('clips.title') }}
          </NuxtLink>
          <h1>{{ t('clips.ops.title') }}</h1>
        </div>
        <button class="clips-ops-refresh" type="button" :disabled="isLoading" @click="loadClipOpsReadiness">
          <span class="i-lucide-refresh-cw" aria-hidden="true"></span>
          {{ t('clips.ops.refresh') }}
        </button>
      </header>

      <p v-if="errorMessage" role="alert" class="clips-ops-error">{{ errorMessage }}</p>

      <section v-if="readiness" class="clips-ops-grid">
        <article class="clips-ops-panel clips-ops-status" :class="statusClass">
          <p>{{ t('clips.ops.status') }}</p>
          <strong>{{ readiness.status }}</strong>
          <span>{{ readiness.next_actions[0] || t('clips.ops.review') }}</span>
        </article>

        <article class="clips-ops-panel">
          <div class="clips-ops-panel-head">
            <span class="i-lucide-route" aria-hidden="true"></span>
            <h2>{{ t('clips.ops.provider') }}</h2>
          </div>
          <dl class="clips-ops-defs">
            <div>
              <dt>{{ t('clips.ops.primary') }}</dt>
              <dd>{{ readiness.provider.primary_provider }}</dd>
            </div>
            <div>
              <dt>{{ t('clips.ops.fallback') }}</dt>
              <dd>{{ readiness.provider.fallback_provider || '-' }}</dd>
            </div>
            <div>
              <dt>{{ t('clips.ops.autoSwitch') }}</dt>
              <dd>{{ readiness.provider.auto_provider_switch ? t('clips.ops.enabled') : t('clips.ops.disabled') }}</dd>
            </div>
          </dl>
          <div class="clips-ops-provider-list">
            <span
              v-for="provider in readiness.provider.providers"
              :key="provider.provider"
              class="clips-ops-provider"
              :class="{ enabled: provider.enabled, candidate: provider.candidate }"
            >
              {{ provider.label }}
            </span>
          </div>
        </article>

        <article class="clips-ops-panel">
          <div class="clips-ops-panel-head">
            <span class="i-lucide-gauge" aria-hidden="true"></span>
            <h2>{{ t('clips.ops.limits') }}</h2>
          </div>
          <div class="clips-ops-stats">
            <div>
              <span>{{ t('clips.ops.batchVariants') }}</span>
              <strong>{{ readiness.limits.max_batch_variants }}</strong>
            </div>
            <div>
              <span>{{ t('clips.ops.dailyPackages') }}</span>
              <strong>{{ readiness.limits.max_daily_packages_per_user }}</strong>
            </div>
            <div>
              <span>{{ t('clips.ops.dailyVariants') }}</span>
              <strong>{{ readiness.limits.max_daily_batch_variants }}</strong>
            </div>
            <div>
              <span>{{ t('clips.ops.costCap') }}</span>
              <strong>{{ readiness.limits.max_estimated_provider_cost_rmb }}</strong>
            </div>
          </div>
        </article>

        <article class="clips-ops-panel">
          <div class="clips-ops-panel-head">
            <span class="i-lucide-activity" aria-hidden="true"></span>
            <h2>{{ t('clips.ops.signals') }}</h2>
          </div>
          <div class="clips-ops-stats">
            <div>
              <span>{{ t('clips.ops.packages') }}</span>
              <strong>{{ readiness.signals.packages_created }}</strong>
            </div>
            <div>
              <span>{{ t('clips.ops.batches') }}</span>
              <strong>{{ readiness.signals.batches_created }}</strong>
            </div>
            <div>
              <span>{{ t('clips.ops.variants') }}</span>
              <strong>{{ readiness.signals.batch_variants_created }}</strong>
            </div>
            <div>
              <span>{{ t('clips.ops.events') }}</span>
              <strong>{{ readiness.signals.clip_events }}</strong>
            </div>
          </div>
        </article>

        <section class="clips-ops-panel clips-ops-wide">
          <div class="clips-ops-panel-head">
            <span class="i-lucide-list-checks" aria-hidden="true"></span>
            <h2>{{ t('clips.ops.gates') }}</h2>
          </div>
          <div class="clips-ops-gates">
            <article v-for="gate in readiness.gates" :key="gate.id" class="clips-ops-gate" :class="`gate-${gate.status}`">
              <strong>{{ gate.label }}</strong>
              <span>{{ gate.status }}</span>
              <p>{{ gate.detail }}</p>
            </article>
          </div>
        </section>

        <section class="clips-ops-panel clips-ops-wide">
          <div class="clips-ops-lists">
            <div>
              <h2>{{ t('clips.ops.blockers') }}</h2>
              <p v-if="!readiness.blockers.length" class="clips-ops-muted">{{ t('clips.ops.none') }}</p>
              <ul v-else>
                <li v-for="item in readiness.blockers" :key="item">{{ item }}</li>
              </ul>
            </div>
            <div>
              <h2>{{ t('clips.ops.warnings') }}</h2>
              <p v-if="!readiness.warnings.length" class="clips-ops-muted">{{ t('clips.ops.none') }}</p>
              <ul v-else>
                <li v-for="item in readiness.warnings" :key="item">{{ item }}</li>
              </ul>
            </div>
            <div>
              <h2>{{ t('clips.ops.nextActions') }}</h2>
              <ul>
                <li v-for="item in readiness.next_actions" :key="item">{{ item }}</li>
              </ul>
            </div>
          </div>
        </section>
      </section>

      <section v-else class="clips-ops-panel">
        <p role="status" class="clips-ops-muted">{{ t('clips.ops.loading') }}</p>
      </section>
    </section>
  </div>
</template>

<script setup lang="ts">
import '~/components/mm/clips/clips.css';
import { computed, onMounted, ref } from 'vue';
import musicService, { type ClipOpsReadinessPayload } from '~/services/musicService';

const { t } = useI18n();
const readiness = ref<ClipOpsReadinessPayload | null>(null);
const isLoading = ref(false);
const errorMessage = ref('');

const statusClass = computed(() => {
  if (!readiness.value) return 'status-monitor';
  if (readiness.value.status === 'ready') return 'status-ready';
  if (readiness.value.status === 'blocked') return 'status-blocked';
  return 'status-monitor';
});

const unwrapData = <T,>(payload: any): T => {
  return (payload?.data ?? payload) as T;
};

const loadClipOpsReadiness = async () => {
  errorMessage.value = '';
  isLoading.value = true;
  try {
    const response = await musicService.getClipOpsReadiness();
    readiness.value = unwrapData<ClipOpsReadinessPayload>(response);
  } catch (error: any) {
    errorMessage.value = error?.message || String(t('clips.ops.loadFailed'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadClipOpsReadiness);
</script>
